import React, { useEffect, useMemo, useState } from "react";
import {
  FiCreditCard,
  FiTruck,
  FiShoppingCart,
  FiSave,
  FiSettings,
} from "react-icons/fi";
import { toast } from "react-toastify";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";
import customloader from "../../assets/images/RollingLoader.gif";
import "./MarketplaceSettings.css";

// Presentational metadata per option_group. Any group added to the backend
// registry renders automatically; this map only customizes title/icon.
const GROUP_META = {
  payment: { title: "Payment Methods", icon: <FiCreditCard /> },
  checkout: { title: "Checkout", icon: <FiShoppingCart /> },
  delivery: { title: "Delivery", icon: <FiTruck /> },
};

const PAYMENT_METHOD_LABELS = {
  cod: "Cash on Delivery",
  card: "Card",
  bank_transfer: "Bank Transfer",
  raast: "Raast",
  bnpl: "BNPL (Card)",
};

const groupTitle = (group) =>
  group
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const MarketplaceSettings = () => {
  const fetchOptionsCall = useApi(api.fetchMarketplaceOptions, { hasCatchError: true });
  const updateOptionsCall = useApi(postapi.updateMarketplaceOptions, { hasCatchError: true });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState([]);
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});

  const fetchOptions = async () => {
    try {
      setIsLoading(true);
      const res = await fetchOptionsCall.request();
      const data = res?.data?.data || [];
      setOptions(data);
      const map = {};
      data.forEach((o) => {
        map[o.key] = o.value;
      });
      setValues(map);
      setInitialValues(map);
    } catch (err) {
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const grouped = useMemo(() => {
    const order = Object.keys(GROUP_META);
    const groups = {};
    options.forEach((o) => {
      if (!groups[o.group]) groups[o.group] = [];
      groups[o.group].push(o);
    });
    const known = order.filter((g) => groups[g]);
    const rest = Object.keys(groups).filter((g) => !order.includes(g));
    return [...known, ...rest].map((g) => ({ group: g, items: groups[g] }));
  }, [options]);

  const dirtyKeys = useMemo(
    () => Object.keys(values).filter((k) => values[k] !== initialValues[k]),
    [values, initialValues]
  );

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const renderControl = (option) => {
    const value = values[option.key];

    if (option.type === "boolean") {
      return (
        <>
          <span className={`ms-status-pill ${value ? "ms-status-on" : "ms-status-off"}`}>
            {value ? "Enabled" : "Disabled"}
          </span>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input ms-switch"
              type="checkbox"
              role="switch"
              id={option.key}
              checked={!!value}
              onChange={(e) => setValue(option.key, e.target.checked)}
            />
          </div>
        </>
      );
    }

    if (option.type === "enum") {
      return (
        <select
          className="ms-select"
          value={value ?? ""}
          onChange={(e) => setValue(option.key, e.target.value)}
        >
          {(option.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {PAYMENT_METHOD_LABELS[opt] || opt}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        className="ms-input"
        type="number"
        min={0}
        value={value ?? 0}
        onChange={(e) => setValue(option.key, Number(e.target.value))}
      />
    );
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      setError("");
      const payload = {};
      dirtyKeys.forEach((k) => {
        payload[k] = values[k];
      });
      const res = await updateOptionsCall.request(payload);
      if (res?.status === 200) {
        toast.success("Marketplace options updated successfully", {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        await fetchOptions();
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || "Failed to update options");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="ms-page-header">
        <div className="ms-page-identity">
          <span className="ms-page-icon">
            <FiSettings />
          </span>
          <h1 className="ms-page-title">Marketplace Settings</h1>
          <span className="ms-page-subtitle">Marketplace-wide options applied on techbazaar.pk</span>
        </div>
        <button
          type="button"
          className="ms-btn-save"
          onClick={handleSubmit}
          disabled={dirtyKeys.length === 0 || isSaving}
        >
          <FiSave /> {isSaving ? "Saving..." : `Save Changes${dirtyKeys.length ? ` (${dirtyKeys.length})` : ""}`}
        </button>
      </div>

      {error && <div className="ms-error">*{error}</div>}

      <div className="ms-page-body">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
            <img src={customloader} alt="customloader" width="100" height="100" />
          </div>
        ) : (
          grouped.map(({ group, items }) => (
            <div className="ms-card" key={group}>
              <div className="ms-card-header">
                {GROUP_META[group]?.icon || <FiSettings />}
                {GROUP_META[group]?.title || groupTitle(group)}
              </div>
              {items.map((option) => (
                <div className="ms-option-row" key={option.key}>
                  <div>
                    <div className="ms-option-label">{option.label}</div>
                    <div className="ms-option-desc">{option.description}</div>
                    {option.updated_at && (
                      <div className="ms-updated-at">
                        Last updated {new Date(option.updated_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="ms-option-control">{renderControl(option)}</div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MarketplaceSettings;
